import React, { useEffect, useState } from "react";
import { TextField } from "@mui/material";
import { FilterLineIcon } from "../../../components/common/Icons/Icons";
import { useSearchParams } from "react-router-dom";
import ManageApiKeysTable from "./ManageApiKeysTable";

interface ContainerProps {
  setGenerateOpen: any;
  setRevokeOpen: any;
  setKeyToModify: any;
}

const ManageApiKeysContainer: React.FC<ContainerProps> = ({
  setGenerateOpen,
  setRevokeOpen,
  setKeyToModify,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Open by default: every filter in view
  const [showFilters, setShowFilters] = useState(true);

  // Pending filter state — only committed to the URL (and so to the fetch) on Apply.
  const [spaGuid, setSpaGuid] = useState("");
  const [serial, setSerial] = useState("");
  const [revoked, setRevoked] = useState("all");

  useEffect(() => {
    setSpaGuid(searchParams.get("spa_guid") ?? "");
    setSerial(searchParams.get("serial") ?? "");
    setRevoked(searchParams.get("revoked") ?? "all");
  }, [searchParams]);

  const setOrDelete = (params: URLSearchParams, key: string, value: string) => {
    value === "" ? params.delete(key) : params.set(key, value);
  };

  const handleApply = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("page"); // reset page
    setOrDelete(next, "spa_guid", spaGuid.trim());
    setOrDelete(next, "serial", serial.trim());
    revoked === "all" ? next.delete("revoked") : next.set("revoked", revoked);
    setSearchParams(next);
  };

  const handleClear = () => {
    setSpaGuid("");
    setSerial("");
    setRevoked("all");
    const next = new URLSearchParams(searchParams);
    ["page", "spa_guid", "serial", "revoked"].forEach((k) => next.delete(k));
    setSearchParams(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleApply();
  };

  const activeFilters = ["spa_guid", "serial", "revoked"].filter((param) => searchParams.has(param)).length;

  const toolbar = (
    <button
      type="button"
      className={`btn btn-sm dt-tool-btn lp-filter-toggle ${showFilters ? 'active' : ''}`}
      onClick={() => setShowFilters(!showFilters)}
      aria-expanded={showFilters}
      aria-controls="lp-filters"
    >
      <FilterLineIcon/> Filters
      {activeFilters > 0 && <span className="lp-count">{activeFilters}</span>}
    </button>
  );

  return (
    <div className={`lp-layout ${showFilters ? '' : 'filters-hidden'}`}>
      {showFilters && (
        <aside className="lp-filters" id="lp-filters" aria-label="Filters">
          <div className="lp-filters-body">
            <div className="lp-group">
              <TextField
                id="spa-guid-search"
                label="Spa GUID"
                type="search"
                fullWidth
                value={spaGuid}
                onChange={(e) => setSpaGuid(e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
              />
              <TextField
                id="serial-search"
                label="Serial"
                type="search"
                fullWidth
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
              />

              <div className="segmented lp-segmented" role="group" aria-label="Status">
                {[["all", "All"], ["0", "Active"], ["1", "Revoked"]].map(([value, label]) => (
                  <button key={value} type="button" className={revoked === value ? "active" : ""} aria-pressed={revoked === value} onClick={() => setRevoked(value)}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="lp-filters-foot">
            <button type="button" className="btn btn-sm lp-clear-btn" onClick={handleClear}>Clear</button>
            <button type="button" className="btn btn-sm btn-primary" onClick={handleApply}>Apply Filters</button>
          </div>
        </aside>
      )}

      <ManageApiKeysTable
        setGenerateOpen={setGenerateOpen}
        setRevokeOpen={setRevokeOpen}
        setKeyToModify={setKeyToModify}
        toolbar={toolbar}
      />
    </div>
  );
};

export default ManageApiKeysContainer;
